import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments';
import { User } from '../interfaces/user.interface';
import {
  OneSignalService,
  PushSubscriptionState,
} from './one-signal.service';

interface PushSubscriptionPayload {
  provider: 'onesignal';
  subscriptionId?: string;
  token?: string;
  platform: 'web';
  userAgent?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private readonly platformId = inject(PLATFORM_ID);
  private currentUserId?: string;
  private readonly apiUrl = `${environment.apiUrl}/notifications/push-subscriptions`;

  constructor(
    private readonly http: HttpClient,
    private readonly oneSignalService: OneSignalService
  ) {}

  isAvailable(): boolean {
    return this.oneSignalService.isAvailable();
  }

  async identifyUser(user: User | null | undefined): Promise<void> {
    if (!user?._id || !this.isAvailable()) return;

    this.currentUserId = user._id;
    await this.oneSignalService.login(user._id);

    const state = await this.oneSignalService.getState();
    if (state.subscriptionId || state.token) {
      await this.syncSubscription(state);
    }
  }

  async enableForCurrentUser(): Promise<PushSubscriptionState | null> {
    if (!this.isAvailable()) {
      return null;
    }

    const permissionGranted = await this.oneSignalService.requestPermission();
    if (!permissionGranted) {
      return await this.oneSignalService.getState();
    }

    const state = await this.oneSignalService.optIn();
    if (state?.subscriptionId || state?.token) {
      await this.syncSubscription(state);
    }

    return state;
  }

  async disableForCurrentBrowser(): Promise<void> {
    if (!this.isAvailable()) return;

    const state = await this.oneSignalService.getState();
    await this.oneSignalService.optOut();

    if (state.subscriptionId) {
      await firstValueFrom(
        this.http
          .delete(`${this.apiUrl}/${encodeURIComponent(state.subscriptionId)}`)
          .pipe(catchError(() => of(null)))
      );
    }
  }

  async logout(): Promise<void> {
    if (!this.isAvailable()) return;
    this.currentUserId = undefined;
    await this.oneSignalService.logout();
  }

  private async syncSubscription(
    state: PushSubscriptionState
  ): Promise<void> {
    if (!this.currentUserId) return;

    const payload: PushSubscriptionPayload = {
      provider: 'onesignal',
      subscriptionId: state.subscriptionId,
      token: state.token,
      platform: 'web',
      userAgent: this.getUserAgent(),
    };

    await firstValueFrom(
      this.http.post(this.apiUrl, payload).pipe(catchError(() => of(null)))
    );
  }

  private getUserAgent(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined;
    return window.navigator.userAgent;
  }
}
